// @ts-nocheck

/**
 * Creates a GeoJSON point feature from asset data
 * 
 * @param asset The asset object containing location and metadata
 * @param color The color to assign to this feature (usually based on forecast value)
 * @param forecastValue The current forecast value for this asset
 * @returns A GeoJSON Feature object with Point geometry, or null if coordinates are invalid
 */
export const createPointFeature = (asset: any, color: string, forecastValue: number | null) => {
  // Handle cases where lat/lng might be strings or numbers
  let lat = asset.lat;
  let lng = asset.lng;
  
  // Ensure we have valid coordinates
  if (lat === undefined || lng === undefined) {
    console.error("Missing coordinates for asset:", asset.name, asset.id);
    return null;
  }
  
  // Convert to numbers if they're strings
  lat = Number(lat);
  lng = Number(lng);
  
  if (isNaN(lat) || isNaN(lng)) {
    console.error("Invalid coordinates for asset:", asset.name, asset.id, lat, lng);
    return null;
  }
  
  return {
    type: "Feature",
    geometry: {
      type: "Point",
      coordinates: [lng, lat]
    },
    properties: {
      id: asset.id,
      name: asset.name,
      description: asset.description || '',
      assetGroupId: asset.assetGroupId,
      color: color,
      forecastValue: forecastValue,
      imageUrl: asset.imageUrl,
      ...(asset.icon ? { icon: asset.icon } : {}),
      asset: asset // Include full asset object for AssetImage component
    }
  };
};

/**
 * Creates a GeoJSON feature from asset data, handling various GeoJSON formats
 * 
 * @param asset The asset object containing geojson, location and metadata
 * @param color The color to assign to this feature (usually based on forecast value)
 * @param forecastValue The current forecast value for this asset
 * @returns A GeoJSON Feature object, or null if creation fails
 */
export const createAssetFeature = (asset: any, color: string, forecastValue: number | null) => {
  // If asset has a geojson property and it's valid, use it
  if (asset.geojson) {
    try {
      // Parse the geojson string if needed
      let geojsonData = asset.geojson;
      
      if (typeof geojsonData === "string") {
        geojsonData = JSON.parse(geojsonData);
      }
      
      // Handle all possible GeoJSON structures
      if (geojsonData.type === "FeatureCollection" && geojsonData.features && geojsonData.features.length > 0) {
        // It's a FeatureCollection - extract the first feature
        return {
          type: "Feature",
          geometry: geojsonData.features[0].geometry,
          properties: {
            ...geojsonData.features[0].properties,
            id: asset.id,
            name: asset.name,
            description: asset.description || '',
            assetGroupId: asset.assetGroupId,
            color: color,
            forecastValue: forecastValue,
            imageUrl: asset.imageUrl,
            ...(asset.icon ? { icon: asset.icon } : {}),
            asset: asset // Include full asset object for AssetImage component
          }
        };
      } else if (geojsonData.type === "Feature") {
        // It's already a Feature, just add/update properties
        return {
          ...geojsonData,
          properties: {
            ...geojsonData.properties,
            id: asset.id,
            name: asset.name,
            description: asset.description || '',
            assetGroupId: asset.assetGroupId,
            color: color,
            forecastValue: forecastValue,
            imageUrl: asset.imageUrl,
            ...(asset.icon ? { icon: asset.icon } : {}),
            asset: asset // Include full asset object for AssetImage component
          }
        };
      } else if (["Point", "MultiPoint", "LineString", "MultiLineString", "Polygon", "MultiPolygon"].includes(geojsonData.type)) {
        // It's a raw geometry, wrap it in a Feature
        return {
          type: "Feature",
          geometry: geojsonData,
          properties: {
            id: asset.id,
            name: asset.name,
            description: asset.description || '',
            assetGroupId: asset.assetGroupId,
            color: color,
            forecastValue: forecastValue,
            imageUrl: asset.imageUrl,
            ...(asset.icon ? { icon: asset.icon } : {}),
            asset: asset // Include full asset object for AssetImage component
          }
        };
      } else {
        console.error("Unknown GeoJSON structure:", geojsonData);
        return createPointFeature(asset, color, forecastValue);
      }
    } catch (error) {
      console.error("Error parsing GeoJSON data:", error, asset.id);
      // Fallback to point if geojson is invalid
      return createPointFeature(asset, color, forecastValue);
    }
  } else {
    // Create a point feature from lat/lng
    return createPointFeature(asset, color, forecastValue);
  }
};